import React from 'react';
import { Button, Card, Col, Nav, Tab, Table, Row, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleEnterKeyPress, hasPermissions, isAdminView } from '../../utils';
import PageTitle from '../common/PageTitle';

export function AppCardHeader({ children, setShowFreezeModal, app, t, user }) {
  const canFreeze = () => {
    if (isAdminView()) return true;
    return hasPermissions(user, 'apps', 'Write');
  };
  const navigate = useNavigate();

  return (
    <Card className="loaded">
      <Card.Header className="d-flex justify-content-between align-items-center space p-4">
        <h4
          className="text-truncate-on-mobile"
          onClick={() => navigate(`/app/${app.id}/detail`)}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
          role="button"
          tabIndex={0}
          onKeyDown={() => handleEnterKeyPress(navigate(`/app/${app.id}/detail`))}
        >
          {isAdminView() ? (
            <span className="font-normal">{app.nickname}</span>
          ) : (
            <>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-name" className="custom-tooltip">
                    Name
                  </Tooltip>
                }
              >
                <span className="font-normal hover-underline">{app.brand_name}</span>
              </OverlayTrigger>
              <span className="mx-2">&#47; </span>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-handle" className="custom-tooltip">
                    Handle
                  </Tooltip>
                }
              >
                <span className="font-normal hover-underline">{app.handle}</span>
              </OverlayTrigger>
            </>
          )}
        </h4>
        <div className="d-flex">
          <p
            className={`text-lg ${
              !app.frozen ? 'text-success' : 'text-danger'
            } ms-0 me-4 mb-0 d-flex text-start`}
          >
            {!app.frozen ? t('common:status.enabled') : t('common:status.disabled')}
          </p>
          {canFreeze() && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setShowFreezeModal(true);
              }}
              className="generate me-0 rounded-1 py-1"
              variant="primary"
              size="sm"
            >
              {isAdminView()
                ? t(`common:freezeUnfreeze.modal.modify.button`)
                : t(`common:buttons.${app.frozen === true ? 'unfreeze' : 'freeze'}`)}
            </Button>
          )}
          {children}
        </div>
      </Card.Header>
    </Card>
  );
}

export function AppCardBody({ children, header, setHeader }) {
  return (
    <Card.Body as={Row} className="p-0 bg-light g-0">
      <Tab.Container activeKey={header} defaultActiveKey={header} onSelect={(h) => setHeader(h)}>
        {children}
      </Tab.Container>
    </Card.Body>
  );
}

export function AppProfileNavItem({ type, appId, t, route, setHeader = null, disabled = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSelect = () => {
    if (isAdminView() && !setHeader) {
      navigate(`/admin/app/${appId}/${route}`);
    } else if (!location.pathname.includes('/apps')) {
      navigate(`/app/${appId}/${route}`);
    } else {
      setHeader(route);
    }
  };

  return (
    <Nav.Item className="w-100">
      <Nav.Link
        className={`d-block px-4 py-3 ${
          type === route ? ' bg-primary-accent text-primary' : 'text-info'
        }${disabled ? ' opacity-25' : ''}`}
        as="div"
        onClick={!disabled ? handleSelect : undefined}
      >
        {isAdminView()
          ? t(`admin.applications.navigation.${route}`)
          : t(`applications.buttons.${route}`)}
      </Nav.Link>
    </Nav.Item>
  );
}

export function AppProfileTabColumn({ children }) {
  return (
    <Col md="4" lg="3" xl="2" className="border-end">
      <Nav className="d-flex flex-column align-content-start h-100 pd-4">{children}</Nav>
    </Col>
  );
}

export function AppProfileDetailColumn({ children }) {
  return (
    <Col md="8" lg="9" xl="10" className="py-4">
      <Nav className="d-flex flex-column align-content-start h-100 pd-4">{children}</Nav>
    </Col>
  );
}

export function AdminAppDetailView({ app, t }) {
  return (
    <div className="overflow-auto mb-4 border-bottom" style={{ maxHeight: '130px' }}>
      <Table className="custom-detail">
        <thead>
          <tr>
            <th className="text-primary pt-0 pb-1 border-start">
              {t('admin.applications.detail.labels.ownerApp')}
            </th>
            <th className="text-primary pt-0 pb-1">
              {t('admin.applications.detail.labels.ownerEmail')}
            </th>
            <th className="text-primary pt-0 pb-1 border-end">
              {t('admin.applications.detail.labels.ownerPhone')}
            </th>
          </tr>
        </thead>
        <tbody>
          {app.team_members
            .filter((member) => member.role_name === 'owner')
            .map((member) => (
              <tr key={member.login_handle}>
                <td className="text-info py-1 border-start">{member.login_name}</td>
                <td
                  className="text-info py-1"
                  style={{ maxWidth: '500px', wordWrap: 'break-word', whiteSpace: 'normal' }}
                >
                  {member.login_emails.length ? (
                    member.login_emails.map(
                      ({ email }, index) =>
                        `${email}${member.login_emails.length - 1 !== index ? ', ' : ''}`
                    )
                  ) : (
                    <span className="text-info">{t('common:labels.na')}</span>
                  )}
                </td>
                <td className="text-info py-1 border-end">
                  {member.login_phones.length ? (
                    member.login_phones.map(
                      ({ phone }, index) =>
                        `${phone}${member.login_phones.length - 1 !== index ? ', ' : ''}`
                    )
                  ) : (
                    <span className="text-info">{t('common:labels.na')}</span>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
    </div>
  );
}

export function AppDetailHeader({ t }) {
  const url = isAdminView() ? '/admin/apps' : '/apps';
  return (
    <Row className="g-0 page-header mb-3 mb-md-5">
      <PageTitle backBtn={url} title={t('admin.applications.detail.title')} />
    </Row>
  );
}
